/**
 * Terminar de recibir un sitio.
 *
 * Aceptar un traspaso mueve las filas y el sitio sigue en pie, así que todo
 * PARECE terminado. No lo está: hay tres cosas que el traspaso deja rotas o a
 * medias y que nadie arregla solo, porque ninguna se anuncia en el producto.
 *
 *   · Cobros. La cuenta de Stripe se desconecta —es el banco de otra persona—,
 *     así que un sitio que vendía deja de poder cobrar.
 *   · Identidad legal. La razón social, el domicilio y la identificación fiscal
 *     viven en el proyecto y no en la cuenta, así que VIAJAN: el aviso de
 *     privacidad y los términos siguen nombrando al dueño anterior.
 *   · Plan. El de quien recibe puede no dar para lo que acaba de llegar, y hay
 *     una gracia contada antes de que el sitio se despublique.
 *
 * ── Por qué un asistente y no sólo la lista del panel ───────────────────────
 *
 * Porque el momento importa. Justo después de aceptar es el único rato en que
 * alguien tiene la cabeza puesta en este sitio y en nada más; mandarlo al taller
 * con un aviso arriba es pedirle que se acuerde de volver, y volver a un aviso
 * es exactamente lo que nadie hace. Aquí los datos del negocio se escriben en
 * esta misma pantalla —no se navega a una sección a buscarlos— y los cobros se
 * conectan desde el paso.
 *
 * La lista del panel (`ProjectHandover`) sigue existiendo y no sobra: es lo que
 * recoge a quien omite. Las dos leen el mismo estado del servidor, así que lo
 * que se resuelva aquí desaparece de allí sin que nadie las coordine.
 *
 * ── Por qué se puede omitir todo ────────────────────────────────────────────
 *
 * Porque ninguna de las tres cosas es asunto nuestro. Puede que no vaya a
 * vender, que los datos fiscales que ya están sean los suyos, o que quiera mirar
 * el sitio antes de pagar un plan. Un asistente que no deja salir convierte una
 * entrega en un peaje, y quien lo sufre es el cliente de nuestro cliente.
 */
import { useCallback, useEffect, useMemo, useState } from 'react'
import {
  IconArrowLeft,
  IconArrowRight,
  IconBuildingStore,
  IconCheck,
  IconExternalLink,
  IconLoader2,
  IconMinus,
  IconRosetteDiscountCheck,
} from '@tabler/icons-react'
import { VelticaMascot } from '@webbuilder/ui'
import { finishHandover, getBusinessInfo, getHandover, getStripeConnection, saveBusinessInfo, startStripeOnboarding, type BusinessInfo, type Handover } from '../lib/api'
import { SectionLoader, useRecordLoader } from '../components/RecordLoader'
import { useI18n, translateServerError } from '../lib/i18n'
import { longDateNoYear } from '../lib/dates'
import { canConnectStripe, shortfallList } from '../lib/planLimits'
import { StripeMark } from '../components/ProjectWorkspace/sections/StripeMark'
import { useScrollTopOnStep } from '../lib/useScrollTopOnStep'

type StepKey = 'payments' | 'business' | 'plan'

/**
 * Los datos que se piden aquí, y sólo esos.
 *
 * `businessInfo` tiene once campos y esta pantalla pide seis. Los otros cinco
 * —identificación fiscal, quién responde por los datos personales, la moneda—
 * casi nadie los tiene a mano el primer día, y una pantalla que los exige acaba
 * con la gente inventándoselos para poder salir. Están todos en la sección de
 * datos del negocio, con calma y sin nadie esperando.
 *
 * Se piden estos porque son los que aparecen IMPRESOS en el sitio: el nombre en
 * cada página, el domicilio y el contacto en el aviso de privacidad y en los
 * términos. Son los que ahora mismo dicen quién no eres.
 */
/* Claves, no texto: constante de módulo. */
const FIELDS: { key: keyof BusinessInfo; label: string; hint?: string; wide?: boolean }[] = [
  { key: 'tradeName', label: 'handover.biz.tradeName', hint: 'handover.biz.tradeNameHint' },
  { key: 'legalName', label: 'handover.biz.legalName', hint: 'handover.biz.legalNameHint' },
  { key: 'address', label: 'handover.biz.address', wide: true },
  { key: 'city', label: 'handover.biz.city' },
  { key: 'state', label: 'handover.biz.state' },
  { key: 'email', label: 'handover.biz.email' },
  { key: 'phone', label: 'handover.biz.phone' },
]

export function ProjectHandoverWizard({ token, projectId, onDone }: {
  token: string
  projectId: string
  /** Al terminar o al salir: abrir el proyecto en el panel. */
  onDone: () => void
}) {
  const { t } = useI18n()
  const [state, setState] = useState<Handover | null>(null)
  const [business, setBusiness] = useState<BusinessInfo | null>(null)
  const [connected, setConnected] = useState<boolean | null>(null)
  const [loading, setLoading] = useState(true)
  const [failed, setFailed] = useState('')
  const loader = useRecordLoader(loading)

  const [at, setAt] = useState(0)
  useScrollTopOnStep(at)
  /* Terminado de recorrer, aparte del índice: el último paso no puede señalarse
     avanzando uno más, porque la posición se recorta al final de la lista y se
     quedaría dando vueltas en él. */
  const [finished, setFinished] = useState(false)
  const [busy, setBusy] = useState(false)
  const [error, setError] = useState<string | null>(null)
  /**
   * Los pasos saltados. Se MARCAN, no se quitan.
   *
   * Quitándolos, la lista de arriba encogía a cada omisión —«1 de 3», «1 de 2»,
   * «1 de 1»— y el recorrido parecía acortarse solo mientras se avanzaba por él.
   * Un indicador de progreso cuyo total cambia deja de ser un indicador: ya no
   * dice cuánto queda, sólo cuánto queda de lo que todavía no has descartado.
   */
  const [skipped, setSkipped] = useState<StepKey[]>([])
  /** Y los resueltos aquí dentro, para marcarlos sin sacarlos de la lista. */
  const [done, setDone] = useState<StepKey[]>([])

  const load = useCallback(async () => {
    try {
      const handover = await getHandover(token, projectId)
      setState(handover)
      if (!handover) return
      /*
       * Los datos del negocio y el estado de Stripe, en paralelo con el repaso.
       *
       * `allSettled` porque son dos añadidos: si la consulta de Stripe falla
       * —es un servidor de fuera— el paso se dibuja como «sin conectar», que es
       * lo peor que puede pasar, y no se lleva por delante toda la pantalla.
       */
      const [info, stripe] = await Promise.allSettled([
        getBusinessInfo(token, projectId),
        getStripeConnection(projectId, token),
      ])
      if (info.status === 'fulfilled') setBusiness(info.value)
      if (stripe.status === 'fulfilled') setConnected(stripe.value.status === 'ready')
    } catch (err) {
      setFailed(translateServerError(err, t('err.openReview')))
    } finally {
      setLoading(false)
    }
  }, [token, projectId, t])

  useEffect(() => { void load() }, [load])

  /*
   * Qué pasos tocan.
   *
   * Salen del estado del servidor y no de una lista fija: alguien que recibe un
   * sitio sin catálogo y con su plan de sobra no tiene nada que hacer con dos de
   * los tres, y enseñárselos para que los omita es hacerle perder el tiempo con
   * problemas que no tiene.
   *
   * Pero una vez decidida, la lista NO se encoge. Lo que se salta se marca y se
   * queda: omitir es un gesto de la persona a mitad del recorrido, y un
   * recorrido que mengua mientras lo recorres deja de decir cuánto queda —era
   * «1 de 3», «1 de 2», «1 de 1»—. `connected` sí sigue contando porque resuelve
   * nada más cargar, antes de que nadie toque nada, y es lo que evita ofrecer el
   * alta a quien vuelve de Stripe con la cuenta ya lista.
   */
  const steps = useMemo<StepKey[]>(() => {
    if (!state) return []
    const list: StepKey[] = []
    /*
     * El PLAN va primero, y no por importancia sino por DEPENDENCIA: conectar
     * Stripe exige plan de pago —ver `assertCanConnectStripe` en el worker—, así
     * que ofrecer los cobros antes es ofrecer una puerta que el paso siguiente
     * vuelve a cerrar. Resuelto el plan, el de cobros ya se puede completar.
     */
    if (!state.plan.fits) list.push('plan')
    if (!(connected ?? state.payments.connected)) list.push('payments')
    if (state.business.filled > 0) list.push('business')
    return list
  }, [state, connected])

  if (loader.visible) {
    return (
      <main className="dash-fullpage-loading">
        <SectionLoader loader={loader} label={t('handover.loading')} />
      </main>
    )
  }

  /* Sin nada pendiente —o sin traspaso que repasar— no hay pantalla que enseñar:
     al proyecto directamente. Pasa cuando alguien vuelve a esta dirección
     después de haberlo terminado. */
  if (failed || !state || finished || steps.length === 0) {
    return (
      <Finished
        projectName={state?.projectName ?? ''}
        token={token}
        projectId={projectId}
        /*
         * Sólo se da el repaso por terminado si NO se omitió nada.
         *
         * Omitir no es resolver: quien se salta los cobros sigue sin poder
         * cobrar, y marcarlo hecho aquí borraría el aviso del inicio, que es
         * justo lo que tenía que recogerlo. Salir sin marcar deja el pendiente
         * donde estaba.
         */
        markDone={skipped.length === 0}
        onDone={onDone}
      />
    )
  }

  const step = steps[Math.min(at, steps.length - 1)] as StepKey
  const position = Math.min(at, steps.length - 1)

  /** Avanza al siguiente, o cierra el repaso si éste era el último. */
  const advance = () => {
    if (position >= steps.length - 1) setFinished(true)
    else setAt(position + 1)
  }

  const next = () => {
    setError(null)
    setDone((current) => (current.includes(step) ? current : [...current, step]))
    advance()
  }

  const skip = () => {
    setError(null)
    setSkipped((current) => (current.includes(step) ? current : [...current, step]))
    /* Ahora SÍ se avanza el índice. Antes no hacía falta —el paso desaparecía de
       la lista y el siguiente ocupaba su sitio—, y ése era justo el efecto que
       encogía el recorrido delante de quien lo estaba recorriendo. */
    advance()
  }

  const connect = async () => {
    setBusy(true)
    setError(null)
    try {
      /* Se sale a Stripe y se vuelve AQUÍ, no a la sección de cobros: quien está
         terminando de recibir un sitio no viene del taller, y devolverlo allí lo
         deja en mitad del repaso sin nada que lo traiga de vuelta. */
      const { url } = await startStripeOnboarding(projectId, token, { returnTo: 'handover' })
      window.location.href = url
    } catch (err) {
      setError(translateServerError(err, t('err.openPaymentsSetup')))
      setBusy(false)
    }
  }

  const saveBusiness = async (patch: Partial<BusinessInfo>) => {
    setBusy(true)
    setError(null)
    try {
      const saved = await saveBusinessInfo(token, projectId, patch)
      setBusiness(saved)
      next()
    } catch (err) {
      setError(translateServerError(err, t('err.saveData')))
    } finally {
      setBusy(false)
    }
  }

  return (
    <main className="dash-handover-wizard dash-loaded-content-in">
      <div className="dash-handover-wizard-inner">
        <header className="dash-handover-wizard-head">
          <VelticaMascot size={52} />
          <p>{t('handover.yoursNow', { name: state.projectName })}</p>
          <h1>{t('handover.title')}</h1>
          <span>
            {steps.length === 1
              ? t('handover.oneLeft')
              : t('handover.manyLeft', { count: steps.length })}
          </span>
        </header>

        {/* Los pasos, para saber dónde se está y cuánto queda. Un asistente sin
            esto es una sucesión de pantallas que no se sabe si tiene fin, y eso
            empuja a cerrarlo. */}
        <ol className="dash-handover-wizard-rail" aria-label={t('handover.stepsAria')}>
          {steps.map((key, index) => {
            /* Tres estados y no dos. Un paso por el que se pasó sin resolverlo
               no está hecho, pero tampoco sigue esperando: decirlo con la misma
               palomita que los demás sería contar por terminado lo que la
               persona decidió dejar pendiente — y ese pendiente reaparece luego
               como aviso en el inicio del proyecto. */
            const mark = skipped.includes(key)
              ? 'is-skipped'
              : done.includes(key)
                ? 'is-done'
                : index === position
                  ? 'is-now'
                  : index < position ? 'is-done' : undefined
            return (
              <li
                key={key}
                className={mark}
                aria-current={index === position ? 'step' : undefined}
              >
                <span aria-hidden="true" data-step-number={index + 1}>
                  {mark === 'is-done' ? <IconCheck size={12} /> : null}
                  {mark === 'is-skipped' ? <IconMinus size={12} /> : null}
                </span>
                {t(STEP_TITLE[key])}
                {mark === 'is-skipped' ? <span className="dash-sr-only">{t('handover.skipped')}</span> : null}
              </li>
            )
          })}
        </ol>

        <section className="dash-handover-wizard-card">
          {step === 'payments' ? (
            <PaymentsStep state={state} busy={busy} onConnect={() => { void connect() }} />
          ) : step === 'business' ? (
            <BusinessStep
              state={state}
              business={business}
              busy={busy}
              onSave={(patch) => { void saveBusiness(patch) }}
            />
          ) : (
            <PlanStep state={state} />
          )}

          {error ? <p className="dash-handover-wizard-error" role="alert">{error}</p> : null}
        </section>

        <div className="dash-handover-wizard-foot">
          {/* La celda vacía sigue haciendo falta cuando no hay «Atrás»: sin ella
              omitir se iría al hueco de la izquierda. */}
          {position > 0 ? (
            <button type="button" className="dash-handover-wizard-back" disabled={busy} onClick={() => { setAt(position - 1) }}>
              <IconArrowLeft size={14} aria-hidden="true" /> {t('handover.back')}
            </button>
          ) : <span />}

          {/* Omitir siempre disponible, y en letra: ninguna de estas tres cosas
              es asunto nuestro, y un asistente que no deja salir convierte una
              entrega en un peaje. Lo que se salte aparece luego como aviso en el
              inicio del proyecto, así que no se pierde — sólo deja de estorbar
              ahora.

              A la derecha, con el resto de lo que hace avanzar. Estaba centrado,
              y ahí no pertenecía a ningún lado: ni al camino de vuelta de la
              izquierda ni a las acciones. Ahora el pie se lee como todos —salir
              hacia atrás a un lado, seguir hacia adelante al otro—. */}
          <button type="button" className="dash-handover-wizard-skip" disabled={busy} onClick={skip}>
            {t(position >= steps.length - 1 ? 'handover.skipAll' : 'handover.skipStep')}
          </button>
        </div>
      </div>
    </main>
  )
}

/* En el mismo orden en que se recorren, que es el de `steps`: el plan primero
   porque los cobros dependen de él. */
const STEP_TITLE: Record<StepKey, string> = {
  plan: 'handover.step.plan',
  payments: 'handover.step.payments',
  business: 'handover.step.business',
}

/**
 * El paso de cobros: el alta ocurre en Stripe, así que aquí sólo se explica.
 *
 * Con plan gratuito enseña otra cosa. Conectar Stripe pide Pro o Business —ver
 * `assertCanConnectStripe` en el worker—, así que ofrecer el botón del alta era
 * mandar a alguien a un trámite de verdad con una empresa de fuera para que el
 * servidor lo rechazara un paso después, con un 402 en rojo bajo el botón. Un
 * botón que en este estado sólo puede fallar no es una opción: es una trampa.
 *
 * Y no basta con reordenar los pasos para que el plan vaya antes: el paso del
 * plan sólo aparece cuando lo recibido NO CABE en el que ya se tiene, así que
 * quien recibe un sitio pequeño con el plan gratis no lo ve nunca y llegaba aquí
 * igual. La reja hay que decirla donde se topa.
 */
function PaymentsStep({ state, busy, onConnect }: {
  state: Handover
  busy: boolean
  onConnect: () => void
}) {
  const { t } = useI18n()
  const needsPlan = !canConnectStripe(state.plan.plan)

  return (
    <>
      <span className="dash-handover-wizard-icon is-stripe" aria-hidden="true"><StripeMark size={21} /></span>
      <h2>{t('handover.pay.title')}</h2>
      <p>{t('handover.pay.body')}</p>

      {needsPlan ? (
        <>
          {/* Lo que se conserva, dicho antes que lo que falta: quien acaba de
              recibir un sitio necesita saber que no se le apaga nada. */}
          <p className="dash-handover-wizard-aside">{t('handover.pay.needsPlan')}</p>
          <div className="dash-handover-wizard-cta">
            <a className="dash-primary-btn" href="/?view=plans" target="_blank" rel="noreferrer">
              <IconRosetteDiscountCheck size={15} /> {t('handover.pay.seePlans')}
            </a>
          </div>
        </>
      ) : (
        <>
          <p className="dash-handover-wizard-aside">{t('handover.pay.aside')}</p>
          <div className="dash-handover-wizard-cta">
            <button type="button" className="dash-primary-btn" disabled={busy} onClick={onConnect}>
              {busy ? <IconLoader2 size={15} className="dash-spin" /> : <IconExternalLink size={15} />}
              {t(busy ? 'handover.pay.opening' : 'handover.pay.connect')}
            </button>
          </div>
        </>
      )}
    </>
  )
}

/**
 * El paso del negocio, con el formulario DENTRO.
 *
 * Es el que justifica que esto sea un asistente y no un aviso: mandar a alguien
 * a una sección de ajustes a buscar un formulario es perderlo. Aquí llega
 * relleno con lo que hay —los datos del anterior dueño— para que se vea de qué
 * se está hablando, y se escribe encima.
 */
function BusinessStep({ state, business, busy, onSave }: {
  state: Handover
  business: BusinessInfo | null
  busy: boolean
  onSave: (patch: Partial<BusinessInfo>) => void
}) {
  const [draft, setDraft] = useState<Partial<BusinessInfo>>({})

  /* Se copia una vez, cuando llegan: después manda lo escrito. Sin esto, cada
     respuesta del servidor pisaría lo que se estuviera tecleando. */
  useEffect(() => {
    if (business) setDraft({ ...business })
  }, [business])

  const { t } = useI18n()
  const previous = state.business.tradeName || state.business.legalName

  return (
    <>
      <span className="dash-handover-wizard-icon" aria-hidden="true"><IconBuildingStore size={20} /></span>
      <h2>{t('handover.biz.title')}</h2>
      {/* Partido porque el nombre anterior va en negrita en medio de la frase. */}
      <p>
        {previous
          ? <>{t('handover.biz.stillSays')} <strong>«{previous}»</strong>{t('handover.biz.withAddress')}</>
          : <>{t('handover.biz.fromGiver')}</>}
        {' '}{t('handover.biz.traveled')}
      </p>

      <form
        className="dash-handover-wizard-form"
        onSubmit={(event) => { event.preventDefault(); onSave(draft) }}
      >
        {FIELDS.map((field) => (
          <label key={field.key} className={field.wide ? 'is-wide' : undefined}>
            <span>{t(field.label)}</span>
            <input
              type={field.key === 'email' ? 'email' : field.key === 'phone' ? 'tel' : 'text'}
              value={draft[field.key] ?? ''}
              disabled={busy || !business}
              onChange={(event) => { setDraft((current) => ({ ...current, [field.key]: event.target.value })) }}
            />
            {field.hint ? <small>{t(field.hint)}</small> : null}
          </label>
        ))}

        <p className="dash-handover-wizard-aside is-form">{t('handover.biz.aside')}</p>

        <div className="dash-handover-wizard-cta">
          <button type="submit" className="dash-primary-btn" disabled={busy || !business}>
            {busy ? <IconLoader2 size={15} className="dash-spin" /> : <IconCheck size={15} />}
            {t(busy ? 'handover.biz.saving' : 'handover.biz.save')}
          </button>
        </div>
      </form>
    </>
  )
}

/**
 * El paso del plan.
 *
 * Manda a la sección de planes en otra pestaña en vez de incrustar el pago: el
 * cobro de la membresía es una pantalla propia con su ciclo, sus impuestos y su
 * redirección a Stripe, y meterla dentro de un asistente sería mantener dos
 * caminos al mismo sitio. En otra pestaña, además, no se pierde el repaso.
 */
function PlanStep({ state }: { state: Handover }) {
  const { t } = useI18n()
  const until = state.plan.graceUntil ? longDateNoYear(state.plan.graceUntil) : null

  return (
    <>
      <span className="dash-handover-wizard-icon is-upgrade" aria-hidden="true"><IconRosetteDiscountCheck size={20} /></span>
      <h2>{t('handover.plan.title')}</h2>
      <p>
        {t('handover.plan.body', { list: shortfallList(state.plan.shortfalls, t) })}
        {until ? t('handover.plan.grace', { date: until }) : t('handover.plan.noGrace')}
      </p>
      <p className="dash-handover-wizard-aside">{t('handover.plan.aside')}</p>
      <div className="dash-handover-wizard-cta">
        <a
          className="dash-primary-btn"
          href="/?view=plans"
          target="_blank"
          rel="noreferrer"
        >
          <IconRosetteDiscountCheck size={15} /> {t('handover.plan.upgrade')}
        </a>
      </div>
    </>
  )
}

/** El final: se entra al sitio, y se cierra el repaso sólo si se resolvió. */
function Finished({ projectName, token, projectId, markDone, onDone }: {
  projectName: string
  token: string
  projectId: string
  /** Falso si se omitió algo: entonces el pendiente sigue vivo en el inicio. */
  markDone: boolean
  onDone: () => void
}) {
  const { t } = useI18n()
  const [busy, setBusy] = useState(false)

  const enter = async () => {
    setBusy(true)
    /* Se entra pase lo que pase: si la marca falla, lo peor que ocurre es que el
       aviso del inicio siga ahí un rato más. Dejar a alguien atrapado en una
       pantalla de felicitación por eso sería mucho peor. */
    if (markDone) {
      try { await finishHandover(token, projectId) } catch { /* da igual */ }
    }
    onDone()
  }

  return (
    <main className="dash-handover-wizard dash-loaded-content-in">
      <div className="dash-handover-wizard-inner is-narrow">
        <header className="dash-handover-wizard-head">
          <VelticaMascot size={58} />
          <p>{t('handover.done')}</p>
          <h1>{projectName ? t('handover.doneTitleNamed', { name: projectName }) : t('handover.doneTitle')}</h1>
          <span>{t(markDone ? 'handover.doneAll' : 'handover.donePartial')}</span>
        </header>

        <button type="button" className="dash-primary-btn" disabled={busy} onClick={() => { void enter() }}>
          {busy ? <IconLoader2 size={15} className="dash-spin" /> : <IconArrowRight size={15} />}
          {t(busy ? 'handover.entering' : 'handover.enter')}
        </button>
      </div>
    </main>
  )
}
